import React from 'react';
import { useQuery } from '@tanstack/react-query';
import services from 'services/services';

/**
 * Custom React Query hook to fetch the count of panelists for the current account.
 *
 * @param {Object} [options] - Optional query options
 * @returns {Object} useQuery result object containing:
 *   - data: The panelists count data
 *   - isLoading: Loading state
 *   - isError: Error state
 *   - error: Error object if request failed
 *   - refetch: Function to manually refetch the data
 *   - ...other useQuery properties
 */
export function useAccountPanelistsCountQuery(options = {}) {
    return useQuery({
        queryKey: ['account-panelists-count'],
        queryFn: () => services.getAccountPanelistsCount(),
        ...options
    });
}

/**
 * Higher-order component that injects account panelists count query into a component.
 *
 * @param {React.ComponentType} Component - The component to wrap
 * @param {Object} [options] - Optional query options
 * @returns {React.ComponentType} A new component with accountPanelistsCountQuery prop injected
 *
 * @example
 * const MyComponent = ({ accountPanelistsCountQuery }) => {
 *   const { data, isLoading } = accountPanelistsCountQuery;
 *   // ... use the query data
 * };
 */
export const withAccountPanelistsCountQuery = (Component, options) => props => {
    const accountPanelistsCountQuery = useAccountPanelistsCountQuery(options);

    return <Component {...props} accountPanelistsCountQuery={accountPanelistsCountQuery} />;
};
